import React, { useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { ScreenRow } from "../../../../types";
import { useGetPlansQuery, useGetTaxesQuery } from "../../../../services/subscription";
import ConfirmSubscriptionModal from "./ConfirmSubscriptionModal";

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (details: {
    screenId?: string;
    licenseId?: string;
    targetPlanId?: string;
  }) => void;
  selectedScreen?: ScreenRow | null;
}
// Fetch plans

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedScreen,
}) => {
  const { theme } = useTheme();
  const [showConfirm, setShowConfirm] = useState(false);
  const { data: plansResp, isLoading: plansLoading } = useGetPlansQuery();
  const { data: taxesResp } = useGetTaxesQuery();

  const plans: any[] = Array.isArray((plansResp as any)?.results)
    ? (plansResp as any).results
    : Array.isArray((plansResp as any)?.data?.results)
      ? (plansResp as any).data.results
      : Array.isArray(plansResp)
        ? (plansResp as any[])
        : [];
  const isDark = theme === "dark";

  const targetPlan = React.useMemo(() => {
    if (!Array.isArray(plans)) return null;

    // Use targetPlanId if passed from parent
    const providedTargetId = (selectedScreen as any)?.targetPlanId;
    if (providedTargetId) {
      const p = plans.find((p: any) => p._id === providedTargetId);
      if (p) return p;
    }

    const currentPlan = (selectedScreen as any)?.currentPlan;
    let currentCycle = currentPlan?.cycle;

    // Fallback: Infer cycle from name if missing
    if (!currentCycle && currentPlan?.name) {
      const nameUpper = currentPlan.name.toUpperCase();
      if (nameUpper.includes("MONTHLY")) currentCycle = "MONTHLY";
      else if (nameUpper.includes("ANNUALLY") || nameUpper.includes("ANNUAL") || nameUpper.includes("YEARLY")) currentCycle = "ANNUALLY";
    }

    if (currentCycle) {
      const matchingPlan = plans.find(
        (p: any) =>
          String(p?.type).toUpperCase() === "PREMIUM" && p?.cycle === currentCycle
      );
      if (matchingPlan) return matchingPlan;
    }

    return (
      plans.find((p: any) => String(p?.type).toUpperCase() === "PREMIUM") ||
      null
    );
  }, [plans, selectedScreen]);

  const priceObj = React.useMemo(() => {
    if (!targetPlan?.prices?.length) return null;
    return (
      targetPlan.prices.find((pr: any) => pr.currency === "INR") ||
      targetPlan.prices[0] ||
      null
    );
  }, [targetPlan]);

  const taxesList = (taxesResp as any)?.results || [];
  const selectedOrgCountry = (selectedScreen as any)?.organisation?.country?.toLowerCase().trim();

  const countryMap: Record<string, string> = {
    "united arab emirates": "ae",
    "india": "in",
  };

  const appliedTax = taxesList.find((t: any) => {
    const taxCountry = t.country?.toLowerCase().trim();
    return (
      taxCountry === selectedOrgCountry ||
      taxCountry === countryMap[selectedOrgCountry || ""] ||
      countryMap[taxCountry || ""] === selectedOrgCountry
    );
  });

  const subtotal = Number(priceObj?.price ?? 0);

  // Use fetched tax if available, otherwise fallback to priceObj tax
  const taxPercent = appliedTax
    ? Number(appliedTax.rate ?? 0)
    : Number(priceObj?.tax?.percent ?? 0);

  const isInclusive = appliedTax
    ? !!appliedTax.inclusive
    : !!priceObj?.tax?.inclusive;

  const taxName = appliedTax ? appliedTax.name : (priceObj?.tax?.name || "Taxes");

  const { taxAmount, totalAmount } = React.useMemo(() => {
    let tax = 0;
    let total = subtotal;

    if (taxPercent > 0) {
      if (!isInclusive) {
        tax = subtotal * (taxPercent / 100);
        total = subtotal + tax;
      } else {
        tax = subtotal - subtotal / (1 + taxPercent / 100);
        total = subtotal;
      }
    }
    return { taxAmount: tax, totalAmount: total };
  }, [subtotal, taxPercent, isInclusive]);

  const total = totalAmount;

  const currencySymbols: Record<string, string> = {
    INR: "₹",
    USD: "$",
    AED: "د.إ",
  };
  const currency = priceObj?.currency ?? "INR";
  const currencySymbol = currencySymbols[currency] || currency;

  const formatDateDMY = (value: any): string => {
    if (!value) return "--";
    const tryDate = new Date(value);
    if (!isNaN(tryDate.getTime())) {
      const dd = String(tryDate.getDate()).padStart(2, "0");
      const mm = String(tryDate.getMonth() + 1).padStart(2, "0");
      const yy = String(tryDate.getFullYear()).slice(-2);
      return `${dd}-${mm}-${yy}`;
    }
    const s = String(value);
    const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (m) {
      const dd = m[1].padStart(2, "0");
      const mm = m[2].padStart(2, "0");
      const yy = m[3].slice(-2);
      return `${dd}-${mm}-${yy}`;
    }
    return s;
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" onClick={onClose} />
      <div
        className={`w-80 h-full ${theme === "dark" ? "bg-[#1F1F1F]" : "bg-white"
          } shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`p-3 border-b ${theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
            } flex-shrink-0`}
        >
          <div className="flex items-center justify-between">
            <h3
              className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"
                }`}
            >
              Upgrade License to{" "}
              <span
                className={`${theme === "dark" ? "text-[#B6CB24]" : "text-[#6D790F]"
                  }`}
              >
                Premium
              </span>
            </h3>

            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
                }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 12C7.5 12 12 7.5 12 3C12 7.5 16.5 12 21 12C16.5 12 12 16.5 12 21C12 16.5 7.5 12 3 12Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 19.5C2.83333 19.5 4.5 17.8333 4.5 17C4.5 17.8333 6.16667 19.5 7 19.5C6.16667 19.5 4.5 21.1667 4.5 22C4.5 21.1667 2.83333 19.5 2 19.5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 5C17 5 19 3 19 2C19 3 21 5 22 5C21 5 19 7 19 8C19 7 17 5 16 5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h4
                  className={`text-sm font-medium mb-1 ${theme === "dark" ? "text-[#EDF5C0]" : "text-gray-900"
                    }`}
                >
                  Custom Branding
                </h4>
                <p
                  className={`text-xs leading-relaxed ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Upload your gym’s logo and set unique color themes to match
                  your brand.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 12C7.5 12 12 7.5 12 3C12 7.5 16.5 12 21 12C16.5 12 12 16.5 12 21C12 16.5 7.5 12 3 12Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 19.5C2.83333 19.5 4.5 17.8333 4.5 17C4.5 17.8333 6.16667 19.5 7 19.5C6.16667 19.5 4.5 21.1667 4.5 22C4.5 21.1667 2.83333 19.5 2 19.5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 5C17 5 19 3 19 2C19 3 21 5 22 5C21 5 19 7 19 8C19 7 17 5 16 5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h4
                  className={`text-sm font-medium mb-1 ${theme === "dark" ? "text-[#EDF5C0]" : "text-gray-900"
                    }`}
                >
                  More Gym Types
                </h4>
                <p
                  className={`text-xs leading-relaxed ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Unlock access to CrossFit and advanced training categories.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 12C7.5 12 12 7.5 12 3C12 7.5 16.5 12 21 12C16.5 12 12 16.5 12 21C12 16.5 7.5 12 3 12Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 19.5C2.83333 19.5 4.5 17.8333 4.5 17C4.5 17.8333 6.16667 19.5 7 19.5C6.16667 19.5 4.5 21.1667 4.5 22C4.5 21.1667 2.83333 19.5 2 19.5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 5C17 5 19 3 19 2C19 3 21 5 22 5C21 5 19 7 19 8C19 7 17 5 16 5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h4
                  className={`text-sm font-medium mb-1 ${theme === "dark" ? "text-[#EDF5C0]" : "text-gray-900"
                    }`}
                >
                  Custom Workouts
                </h4>
                <p
                  className={`text-xs leading-relaxed ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Add your own workout videos and create personalized training
                  programs.
                </p>
              </div>
            </div>
          </div>

          {/* Cost Details */}
          <div className="mt-0">
            <h4
              className={`text-base font-semibold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"
                }`}
            >
              Cost Details
            </h4>
   <hr
                className={`my-3 ${theme === "dark" ? "border-gray-600" : "border-gray-300"
                  }`}
              />
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span
                  className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Bill Date
                </span>
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  {formatDateDMY(
                    (selectedScreen as any)?.lastBilledDate ??
                    (selectedScreen as any)?.billDate ??
                    ""
                  )}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span
                  className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  End Date
                </span>
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  {formatDateDMY(
                    (selectedScreen as any)?.endDate ??
                    (selectedScreen as any)?.expiryDate ??
                    ""
                  )}
                </span>
              </div>

              {/* <div className="flex justify-between items-center">
                <span
                  className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  Current Plan
                </span>
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  {(selectedScreen as any)?.currentPlan?.name ?? "--"}
                </span>
              </div> */}


              <hr
                className={`my-3 ${theme === "dark" ? "border-gray-600" : "border-gray-300"
                  }`}
              />

              <div className="flex justify-between items-center">
                <span
                  className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  {targetPlan?.name ?? targetPlan?.type ?? "Type"}
                </span>
                <span
                  className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                 Cost  {currencySymbol}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  Due Total
                </span>
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  {currencySymbol}
                  {subtotal.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  Tax - {taxName} ({taxPercent}%{" "}
                  {isInclusive ? "inc." : "exc."})
                </span>
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  {currencySymbol}
                  {taxAmount.toFixed(2)}
                </span>
              </div>

              <hr
                className={`my-2 ${theme === "dark" ? "border-gray-600" : "border-gray-300"
                  }`}
              />

              <div className="flex justify-between items-center font-semibold">
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  Total
                </span>
                <span
                  className={`${theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                >
                  {currencySymbol}
                  {total.toFixed(2)}
                </span>
              </div>
              <div
                className={`pt-2 text-[10px] leading-snug flex items-start gap-2 ${isDark ? "text-gray-400" : "text-gray-500"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="flex-shrink-0 mt-[1px]"
                >
                  <path
                    d="M7.00033 12.8334C10.222 12.8334 12.8337 10.2217 12.8337 7.00008C12.8337 3.77842 10.222 1.16675 7.00033 1.16675C3.77866 1.16675 1.16699 3.77842 1.16699 7.00008C1.16699 10.2217 3.77866 12.8334 7.00033 12.8334Z"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 9.33325V6.70825"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 4.67356V4.66772"
                    stroke="#C6D92C"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p>
                  Future invoices will be billed on a {targetPlan?.cycle === "ANNUALLY" ? "yearly" : "monthly"} basis. The
                  current amount reflects charges for the remaining days.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div
          className={`p-2 border-t space-y-2 flex-shrink-0 ${theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
            }`}
        >
          <button
            onClick={() => setShowConfirm(true)}
            className={`w-full h-10 text-[11px] font-semibold text-black rounded-[10px] border mb-1 ${isDark ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]" : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            style={{
              border: "1px solid var(--menu-active-border, #C6D92C)",
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
            }}
          >
            Upgrade Now
          </button>
          <button
            onClick={onClose}
            className={`w-full px-2 py-2 font-medium rounded-[10px] border text-sm ${theme === "dark"
              ? "border-gray-600 text-gray-300 hover:bg-white/10"
              : "border-gray-300 text-gray-700 hover:bg-black/5"
              }`}
          >
            Maybe later
          </button>
        </div>
      </div>

      <ConfirmSubscriptionModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          const screenId =
            (selectedScreen as any)?.screenId ||
            (selectedScreen as any)?.screen ||
            (selectedScreen as any)?.code;
          const licenseId =
            (selectedScreen as any)?.license ||
            (selectedScreen as any)?._id ||
            (selectedScreen as any)?.licenseId;
          const targetPlanId =
            (targetPlan as any)?._id || (targetPlan as any)?.id;
          onConfirm({ screenId, licenseId, targetPlanId });
          setShowConfirm(false);
        }}
        title="Upgrade License"
        message={`Are you sure you want to upgrade this license to ${targetPlan?.name || "Premium"}?`}
        confirmText="Confirm and Upgrade"
        iconType="upgrade"
      />
    </div>
  );
};

export default UpgradeModal;
